/**
 * Non-Verbal Reasoning: shape puzzles, number/letter patterns, symbol sequences.
 * Shape format: { t: 'c'|'s'|'t'|'d', f: 0|1, r?: 0|90|180|270 }
 * Text format: stimulus.type === "text" with question, options (strings), answer (string)
 */
const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const pickOne = (arr) => arr[Math.floor(Math.random() * arr.length)];

/** 1. Pattern & Sequence - shapes, numbers, letters, symbols */
const generatePatternSequenceQuestion = () => {
    const useShape = Math.random() < 0.5;
    if (!useShape) {
        const numSeqs = [
            { seq: "2, 4, 6, ?", ans: "8", opts: ["7", "8", "9", "10"] },
            { seq: "1, 3, 5, ?", ans: "7", opts: ["6", "7", "8", "9"] },
            { seq: "3, 6, 9, ?", ans: "12", opts: ["10", "11", "12", "13"] },
            { seq: "5, 10, 15, ?", ans: "20", opts: ["18", "19", "20", "21"] },
            { seq: "A, C, E, ?", ans: "G", opts: ["F", "G", "H", "I"] },
            { seq: "B, D, F, ?", ans: "H", opts: ["G", "H", "I", "J"] },
            { seq: "● ○ ● ○ ?", ans: "●", opts: ["●", "○", "■", "▲"] },
        ];
        const q = pickOne(numSeqs);
        const opts = shuffle([...q.opts]);
        const ansIdx = opts.indexOf(q.ans);
        return { stimulus: { type: "text", question: q.seq }, options: opts, answer: ansIdx >= 0 ? opts[ansIdx] : q.ans, answerIdx: ansIdx >= 0 ? String(ansIdx) : "0", type: "non-verbal", mode: "pattern-sequence", useTextOptions: true };
    }
    const sequences = [
        { seq: [{ t: "c", f: 1 }, { t: "s", f: 1 }, { t: "t", f: 1 }], next: { t: "c", f: 1 }, distractors: [{ t: "s", f: 1 }, { t: "t", f: 1 }, { t: "d", f: 1 }] },
        { seq: [{ t: "s", f: 0 }, { t: "s", f: 1 }, { t: "s", f: 0 }], next: { t: "s", f: 1 }, distractors: [{ t: "s", f: 0 }, { t: "c", f: 1 }, { t: "t", f: 0 }] },
        { seq: [{ t: "t", f: 1, r: 0 }, { t: "t", f: 1, r: 90 }, { t: "t", f: 1, r: 180 }], next: { t: "t", f: 1, r: 270 }, distractors: [{ t: "t", f: 1, r: 0 }, { t: "t", f: 1, r: 90 }, { t: "s", f: 1 }] },
    ];
    const q = sequences[Math.floor(Math.random() * sequences.length)];
    const options = shuffle([q.next, ...q.distractors]);
    const answerIdx = options.indexOf(q.next);
    return { stimulus: { type: "sequence", shapes: q.seq }, options, answer: String(answerIdx), type: "non-verbal", mode: "pattern-sequence", useTextOptions: false };
};

/** 2. Shape Analogies - shapes or number analogies */
const generateShapeAnalogiesQuestion = () => {
    if (Math.random() < 0.5) {
        const numAnalogies = [
            { q: "2 is to 4 as 3 is to ?", ans: "6", opts: ["5", "6", "7", "8"] },
            { q: "3 is to 9 as 4 is to ?", ans: "12", opts: ["10", "11", "12", "13"] },
            { q: "1 is to 2 as 5 is to ?", ans: "10", opts: ["6", "8", "10", "12"] },
        ];
        const a = pickOne(numAnalogies);
        const opts = shuffle([...a.opts]);
        const ansIdx = opts.indexOf(a.ans);
        return { stimulus: { type: "text", question: a.q }, options: opts, answer: a.ans, answerIdx: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "shape-analogies", useTextOptions: true };
    }
    const pairs = [
        { a: { t: "c", f: 1 }, b: { t: "c", f: 0 }, c: { t: "s", f: 1 }, ans: { t: "s", f: 0 }, d: [{ t: "s", f: 1 }, { t: "c", f: 1 }, { t: "t", f: 0 }] },
        { a: { t: "s", f: 0 }, b: { t: "t", f: 0 }, c: { t: "t", f: 0 }, ans: { t: "c", f: 0 }, d: [{ t: "c", f: 1 }, { t: "s", f: 1 }, { t: "d", f: 0 }] },
    ];
    const q = pairs[Math.floor(Math.random() * pairs.length)];
    const options = shuffle([q.ans, ...q.d]);
    const answerIdx = options.indexOf(q.ans);
    return { stimulus: { type: "analogy", pair1: [q.a, q.b], pair2: [q.c] }, options, answer: String(answerIdx), type: "non-verbal", mode: "shape-analogies", useTextOptions: false };
};

/** 3. Odd One Out - shapes, numbers, letters */
const generateOddOneOutQuestion = () => {
    if (Math.random() < 0.5) {
        const textGroups = [
            { q: "Which does not belong? 2, 4, 6, 7", ans: "7", opts: ["2", "4", "6", "7"] },
            { q: "Which does not belong? 3, 6, 9, 10", ans: "10", opts: ["3", "6", "9", "10"] },
            { q: "Which does not belong? A, B, C, 1", ans: "1", opts: ["A", "B", "C", "1"] },
            { q: "Which does not belong? +, −, ×, 5", ans: "5", opts: ["+", "−", "×", "5"] },
        ];
        const t = pickOne(textGroups);
        const opts = shuffle([...t.opts]);
        const ansIdx = opts.indexOf(t.ans);
        return { stimulus: { type: "text", question: t.q }, options: opts, answer: t.ans, answerIdx: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "odd-one-out", useTextOptions: true };
    }
    const groups = [
        { shapes: [{ t: "c", f: 1 }, { t: "c", f: 1 }, { t: "c", f: 1 }, { t: "s", f: 1 }], oddIdx: 3 },
        { shapes: [{ t: "s", f: 0 }, { t: "s", f: 0 }, { t: "s", f: 1 }, { t: "s", f: 0 }], oddIdx: 2 },
    ];
    const q = groups[Math.floor(Math.random() * groups.length)];
    const options = q.shapes.map((_, i) => q.shapes[i]);
    return { stimulus: { type: "oddout", shapes: q.shapes }, options, answer: String(q.oddIdx), type: "non-verbal", mode: "odd-one-out", useTextOptions: false };
};

/** 4. Rotation & Reflection - shapes or direction */
const generateRotationReflectionQuestion = () => {
    if (Math.random() < 0.5) {
        const dirQuestions = [
            { q: "Arrow ↑ turns 90° clockwise. Which way does it point?", ans: "→", opts: ["↑", "→", "↓", "←"] },
            { q: "Arrow → turns 90° anti-clockwise. Which way?", ans: "↑", opts: ["↑", "→", "↓", "←"] },
        ];
        const d = pickOne(dirQuestions);
        const opts = shuffle([...d.opts]);
        const ansIdx = opts.indexOf(d.ans);
        return { stimulus: { type: "text", question: d.q }, options: opts, answer: d.ans, answerIdx: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "rotation-reflection", useTextOptions: true };
    }
    const orig = { t: "t", f: 1, r: 0 };
    const rotated = { t: "t", f: 1, r: 90 };
    const distractors = [{ t: "t", f: 1, r: 0 }, { t: "t", f: 1, r: 180 }, { t: "t", f: 1, r: 270 }];
    const options = shuffle([rotated, ...distractors.filter((d) => d.r !== 90)]);
    const ansIdx = options.findIndex((o) => o.r === 90);
    return { stimulus: { type: "single", shape: orig, prompt: "rotate" }, options, answer: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "rotation-reflection", useTextOptions: false };
};

/** 5. Matrix - shapes or number matrix */
const generateMatrixQuestion = () => {
    if (Math.random() < 0.5) {
        const numMatrix = [
            { q: "Find the missing number:\n1  2\n3  ?", ans: "4", opts: ["3", "4", "5", "6"] },
            { q: "Find the missing number:\n2  4\n6  ?", ans: "8", opts: ["7", "8", "9", "10"] },
        ];
        const m = pickOne(numMatrix);
        const opts = shuffle([...m.opts]);
        const ansIdx = opts.indexOf(m.ans);
        return { stimulus: { type: "text", question: m.q }, options: opts, answer: m.ans, answerIdx: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "matrix", useTextOptions: true };
    }
    const grid = [
        { t: "c", f: 1 }, { t: "s", f: 1 },
        { t: "s", f: 1 }, null,
    ];
    const correct = { t: "c", f: 1 };
    const opts = shuffle([correct, { t: "s", f: 1 }, { t: "t", f: 1 }, { t: "d", f: 1 }]);
    const ansIdx = opts.indexOf(correct);
    return { stimulus: { type: "matrix", shapes: grid, cols: 2 }, options: opts, answer: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "matrix", useTextOptions: false };
};

/** 6. Spatial - shapes or how many cubes */
const generateSpatialQuestion = () => {
    if (Math.random() < 0.5) {
        const spatialQ = [
            { q: "A stack has 2 cubes on bottom, 1 on top. How many cubes?", ans: "3", opts: ["2", "3", "4", "5"] },
            { q: "You see 1 cube. It has 6 faces. How many faces can you see from one side?", ans: "1", opts: ["1", "2", "3", "6"] },
        ];
        const s = pickOne(spatialQ);
        const opts = shuffle([...s.opts]);
        const ansIdx = opts.indexOf(s.ans);
        return { stimulus: { type: "text", question: s.q }, options: opts, answer: s.ans, answerIdx: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "spatial", useTextOptions: true };
    }
    const correct = { t: "s", f: 1 };
    const options = shuffle([correct, { t: "s", f: 0 }, { t: "c", f: 1 }, { t: "t", f: 1 }]);
    const ansIdx = options.indexOf(correct);
    return { stimulus: { type: "sequence", shapes: [{ t: "s", f: 0 }, { t: "s", f: 1 }], prompt: "same" }, options, answer: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "spatial", useTextOptions: false };
};

/** 7. Shape Codes - shapes or letter-number codes */
const generateShapeCodesQuestion = () => {
    if (Math.random() < 0.5) {
        const codeQ = [
            { q: "If A=1, B=2, C=3, what is CAB?", ans: "6", opts: ["5", "6", "7", "123"] },
            { q: "If 1=A, 2=B, 3=C, what letter is 5?", ans: "E", opts: ["D", "E", "F", "G"] },
        ];
        const c = pickOne(codeQ);
        const opts = shuffle([...c.opts]);
        const ansIdx = opts.indexOf(c.ans);
        return { stimulus: { type: "text", question: c.q }, options: opts, answer: c.ans, answerIdx: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "shape-codes", useTextOptions: true };
    }
    const before = [{ t: "c", f: 0 }, { t: "s", f: 0 }];
    const after = { t: "t", f: 1 };
    const opts = shuffle([after, { t: "t", f: 0 }, { t: "c", f: 1 }, { t: "s", f: 0 }]);
    const ansIdx = opts.indexOf(after);
    return { stimulus: { type: "codes", before, after }, options: opts, answer: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "shape-codes", useTextOptions: false };
};

/** 8. Hidden / Composite - shapes or find in pattern */
const generateHiddenShapesQuestion = () => {
    if (Math.random() < 0.5) {
        const hiddenQ = [
            { q: "In 1213121, which number appears 3 times?", ans: "1", opts: ["1", "2", "3", "0"] },
            { q: "In ABCDE, which letter is in the middle?", ans: "C", opts: ["A", "B", "C", "D"] },
        ];
        const h = pickOne(hiddenQ);
        const opts = shuffle([...h.opts]);
        const ansIdx = opts.indexOf(h.ans);
        return { stimulus: { type: "text", question: h.q }, options: opts, answer: h.ans, answerIdx: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "hidden-shapes", useTextOptions: true };
    }
    const composite = [{ t: "c", f: 1 }, { t: "s", f: 0 }];
    const hidden = { t: "c", f: 1 };
    const opts = shuffle([hidden, { t: "s", f: 0 }, { t: "t", f: 1 }, { t: "d", f: 1 }]);
    const ansIdx = opts.indexOf(hidden);
    return { stimulus: { type: "hidden", shapes: composite, find: hidden }, options: opts, answer: String(ansIdx >= 0 ? ansIdx : 0), type: "non-verbal", mode: "hidden-shapes", useTextOptions: false };
};

export const generateNonVerbalReasoningQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "nvr-pattern-sequence": return generatePatternSequenceQuestion();
        case "nvr-shape-analogies": return generateShapeAnalogiesQuestion();
        case "nvr-odd-one-out": return generateOddOneOutQuestion();
        case "nvr-rotation-reflection": return generateRotationReflectionQuestion();
        case "nvr-matrix": return generateMatrixQuestion();
        case "nvr-spatial": return generateSpatialQuestion();
        case "nvr-shape-codes": return generateShapeCodesQuestion();
        case "nvr-hidden-shapes": return generateHiddenShapesQuestion();
        default: return generatePatternSequenceQuestion();
    }
};
